import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '@/lib/api';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Validate the stored token against the backend before trusting it.
    // If the JWT has expired or been revoked, wipe localStorage now so the
    // user sees the login page immediately instead of bouncing in and out
    // of protected routes (which was the "had to retry login" complaint).
    const bootstrap = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (!token || !savedUser) {
        setLoading(false);
        return;
      }

      // Optimistically hydrate from localStorage so the UI renders fast,
      // then re-verify with /auth/me in the background.
      try {
        setUser(JSON.parse(savedUser));
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setLoading(false);
        return;
      }

      try {
        const resp = await axios.get(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        if (resp?.data) {
          // Refresh the cached user payload so roles/company/flags stay current.
          localStorage.setItem('user', JSON.stringify(resp.data));
          setUser(resp.data);
        }
      } catch (err) {
        // Only clear the session on an explicit auth failure. Network
        // errors (offline, 5xx, timeout) must NOT kick the user out.
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, []);

  const login = async (email, password) => {
    const data = await authAPI.login(email, password);
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    // Force a hard navigation so EVERY component in the React tree
    // unmounts. This guarantees no stale in-memory state from the
    // previous user (e.g. another tenant's inbox chats, orders list,
    // analytics charts, polling intervals) can render under the next
    // user that signs in on the same tab — a P0 multi-tenant
    // data-isolation guarantee. SPA-style navigation alone leaves
    // module-level state and component state intact between sessions.
    if (typeof window !== 'undefined') {
      window.location.replace('/login');
    }
  };

  const register = async (userData) => {
    const user = await authAPI.register(userData);
    return user;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, register }}>
      {children}
    </AuthContext.Provider>
  );
};
